/* eslint-disable */
// disabling flow as these are new flow errors from flow-bin upgrade
import React, { Component } from "react";
import { compose } from "../../utils";
// accepts event or persisted event
export type EventHandler = (e?: React.SyntheticEvent<any> | {}) => void;
type FormValues = Record<string, any>;
type FormErrors = Record<string, string>;
export type Field = { [key in any]?: any } & {
  name: string;
  validate: (arg0: FormValues | null | undefined) => FormErrors;
  onClick: EventHandler;
  onFocus: EventHandler;
  onBlur: EventHandler;
  onChange: EventHandler;
};
type RenderedState = {
  dirtySinceLastSubmit: boolean;
  hasAttemptedSubmit: boolean;
  pristine: boolean;
  errors: FormErrors;
  submitError: string;
  submitFailed: boolean;
  submitSucceeded: boolean;
  submitting: boolean;
  values: FormValues;
};
type State = RenderedState & {
  active: Record<string, boolean>;
  initialValues: FormValues;
  lastSubmittedValues: FormValues;
  submitErrors: FormErrors;
  touched: Record<string, boolean>;
};
type SubmitButtonPropsArgs = { [key in any]?: any } & {
  isFetching: boolean;
  onClick?: EventHandler;
};
type SubmitButtonProps = (arg0: SubmitButtonPropsArgs) => { [key in any]?: any } & {
  disabled: boolean;
  isFetching: boolean;
  onClick: EventHandler;
};
export type SetValues = (arg0: FormValues, callback?: () => void) => void;
// this is passed into the child method
export type RenderProps = RenderedState & {
  handleSubmit: (arg0: React.SyntheticEvent<any>) => void;
  setValues: SetValues;
  getSubmitButtonProps: SubmitButtonProps;
};
type Props = {
  initialValues: FormValues;
  onChange?: EventHandler;
  onClick?: EventHandler;
  onBlur?: EventHandler;
  onFocus?: EventHandler;
  onSubmit: (arg0: FormValues) => void;
  // used in handleValidation
  validate?: (arg0: FormValues) => FormErrors;
  fields: Field[];
  children?: (arg0: RenderProps) => React.ReactNode;
  render?: (arg0: RenderProps) => React.ReactNode;
};
export type InputPropsFunc = (arg0: {
  onBlur?: EventHandler;
  onChange?: EventHandler;
  onClick?: EventHandler;
  onFocus?: EventHandler;
} | null | undefined) => { [key in any]?: any } & {
  name: string;
  value: any;
  active: boolean;
  dirtySinceLastSubmit: boolean;
  error: string;
  submitError: string;
  touched: boolean;
  onBlur: EventHandler;
  onChange: EventHandler;
  onClick: EventHandler;
  onFocus: EventHandler;
};
export type GetInputPropsFunc = (name: string) => InputPropsFunc;
export type ContextValue = {
  getInputProps: GetInputPropsFunc;
};
export const FormContext = React.createContext({
  getInputProps: name => () => ({
    name,
    value: null,
    active: false,
    dirtySinceLastSubmit: false,
    error: '',
    submitError: '',
    touched: false,
    onBlur: () => {},
    onChange: () => {},
    onClick: () => {},
    onFocus: () => {}
  })
});

const handleValidation = (values: FormValues, props: Props): FormErrors => props.validate ? props.validate(values) : props.fields.reduce((errors, field) => ({ ...errors,
  [field.name]: field.validate ? field.validate(values[field.name]) : ''
}), {});

class Form extends Component<Props, State> {
  static defaultProps = {
    fields: [],
    onBlur: () => {},
    onChange: () => {},
    onClick: () => {},
    onFocus: () => {},
    onSubmit: () => {}
  };
  initialValues = this.props.initialValues // eslint-disable-line react/destructuring-assignment
  || this.props.fields.reduce( // eslint-disable-line react/destructuring-assignment
  (values, field) => ({ ...values,
    [field.name]: field.initialValue || ''
  }), {});
  state = {
    active: {},
    dirtySinceLastSubmit: false,
    initialValues: this.initialValues,
    errors: handleValidation(this.initialValues, this.props),
    hasAttemptedSubmit: false,
    lastSubmittedValues: {},
    pristine: true,
    submitError: '',
    submitErrors: {},
    submitFailed: false,
    submitSucceeded: false,
    submitting: false,
    touched: Object.keys(this.initialValues).reduce((a, k) => ({ ...a,
      [k]: false
    }), {}),
    values: this.initialValues
  };
  setValues: SetValues = (values, callback = () => {}) => {
    const {
      initialValues,
      lastSubmittedValues
    } = this.state;
    const lastState = this.state;
    const newState = {
      values: { ...lastState.values,
        ...values
      }
    };
    const newStateKeys = Object.keys(newState.values);
    const pristine = newStateKeys.every(key => initialValues[key] === newState.values[key]);
    const dirtySinceLastSubmit = newStateKeys.some(key => lastSubmittedValues[key] !== newState.values[key]);
    const errors = handleValidation(newState.values, this.props);
    this.setState({ ...newState,
      dirtySinceLastSubmit,
      errors,
      pristine
    }, callback);
  };
  handleChange = (event: React.SyntheticEvent<any>) => {
    const {
      onChange
    } = this.props;
    const persistedEvent: Record<string, any> = { ...event
    };
    const {
      currentTarget,
      currentTarget: {
        name,
        type,
        checked
      }
    } = persistedEvent;
    const value = type === 'checkbox' ? checked : currentTarget.value; // checkbox support

    this.setValues({
      [name]: value
    }, () => {
      if (onChange) {
        onChange(persistedEvent);
      }
    });
  };
  handleClick = (event: React.SyntheticEvent<any>) => {
    const {
      onClick
    } = this.props;
    const persistedEvent = { ...event
    };
    this.setState({}, () => {
      if (onClick) {
        onClick(persistedEvent);
      }
    });
  };
  handleBlur = (event: React.SyntheticEvent<any>) => {
    const {
      onBlur
    } = this.props;
    const persistedEvent = { ...event
    };
    const lastState = this.state;
    // fallback to id necessary to support composed fields like date/time picker
    const name = event.currentTarget.name || event.currentTarget.id;
    const newState = {
      touched: { ...lastState.touched,
        [name]: true
      },
      active: { ...lastState.active,
        [name]: false
      }
    };
    this.setState(newState, () => {
      if (onBlur) {
        onBlur(persistedEvent);
      }
    });
  };
  handleFocus = (event: React.SyntheticEvent<any>) => {
    const {
      onFocus
    } = this.props;
    const persistedEvent = { ...event
    };
    const lastState = this.state;
    // fallback to id necessary to support composed fields like date/time picker
    const name = event.currentTarget.name || event.currentTarget.id;
    const newState = {
      active: { ...lastState.active,
        [name]: true
      }
    };
    this.setState(newState, () => {
      if (onFocus) {
        onFocus(persistedEvent);
      }
    });
  };
  setErrors = (errors: FormErrors) => {
    const newState: Record<string, any> = {
      submitting: false
    };

    if (errors && Object.keys(errors).length) {
      newState.submitFailed = true;
      newState.submitSucceeded = false;
      newState.submitErrors = errors;
      newState.submitError = errors.FORM_ERROR;
    } else {
      newState.submitErrors = {};
      newState.submitError = '';
      newState.submitFailed = false;
      newState.submitSucceeded = true;
    }

    this.setState(newState);
  };
  handleSubmit = (event: React.SyntheticEvent<any>) => {
    const {
      onSubmit
    } = this.props;
    const {
      values
    } = this.state;
    event.preventDefault();
    this.setState(prevState => ({
      hasAttemptedSubmit: true,
      submitting: true,
      submitErrors: {},
      submitFailed: false,
      submitSucceeded: false,
      dirtySinceLastSubmit: false,
      lastSubmittedValues: { ...values
      },
      touched: Object.keys(prevState.touched).reduce((a, key) => ({ ...a,
        [key]: true
      }), {})
    }));
    const errors = handleValidation(values, this.props);

    if (Object.values(errors).some(error => !!error)) {
      return this.setErrors(errors);
    }

    return onSubmit(values);
  };
  getSubmitButtonProps = ({
    pristine,
    dirtySinceLastSubmit
  }: {
    pristine: boolean;
    // eslint-disable-line react/no-unused-prop-types
    dirtySinceLastSubmit: boolean; // eslint-disable-line react/no-unused-prop-types

  }) => ({
    isFetching,
    onClick,
    ...customProps
  }: SubmitButtonPropsArgs = {}) => ({
    disabled: isFetching || pristine || !dirtySinceLastSubmit,
    isFetching,
    ...customProps,
    onClick: compose(onClick, this.handleClick)
  });
  getFieldsProps = (name: string) => {
    const {
      fields
    } = this.props;
    return {
      onBlur: () => {},
      onChange: () => {},
      onClick: () => {},
      onFocus: () => {},
      validate: () => {},
      ...fields.find(field => field.name === name)
    };
  };
  getInputProps: GetInputPropsFunc = (name: string) => inputFuncProps => {
    const {
      onBlur,
      onChange,
      onClick,
      onFocus,
      ...customProps
    } = {
      onBlur: () => {},
      onChange: () => {},
      onClick: () => {},
      onFocus: () => {},
      ...(inputFuncProps || {})
    };
    const {
      active,
      errors,
      lastSubmittedValues,
      submitErrors,
      touched,
      values
    } = this.state;
    const {
      validate,
      ...fieldProps
    } = this.getFieldsProps(name);
    return {
      name,
      value: values[name] ? values[name] : '',
      active: active[name] ? active[name] : false,
      dirtySinceLastSubmit: lastSubmittedValues[name] !== values[name],
      error: errors[name] ? errors[name] : '',
      submitError: submitErrors[name] ? submitErrors[name] : '',
      touched: touched[name] ? touched[name] : false,
      ...fieldProps,
      ...customProps,
      onBlur: compose(onBlur, this.getFieldsProps(name).onBlur, this.handleBlur),
      onChange: compose(onChange, this.getFieldsProps(name).onChange, this.handleChange),
      onClick: compose(onClick, this.getFieldsProps(name).onClick, this.handleClick),
      onFocus: compose(onFocus, this.getFieldsProps(name).onFocus, this.handleFocus)
    };
  };

  render() {
    const {
      getInputProps,
      getSubmitButtonProps,
      handleSubmit,
      setValues
    } = this;
    const {
      children,
      render
    } = this.props;
    const {
      dirtySinceLastSubmit,
      errors,
      hasAttemptedSubmit,
      pristine,
      submitError,
      submitFailed,
      submitSucceeded,
      submitting,
      values
    } = this.state;
    const childProps: RenderProps = {
      dirtySinceLastSubmit,
      errors,
      hasAttemptedSubmit,
      handleSubmit,
      pristine,
      setValues,
      submitError,
      submitFailed,
      submitSucceeded,
      submitting,
      values,
      getSubmitButtonProps: getSubmitButtonProps({
        pristine,
        dirtySinceLastSubmit
      })
    };
    return <FormContext.Provider value={{
      getInputProps
    }}>
        {render ? render(childProps) : null}
        {children ? children(childProps) : null}
      </FormContext.Provider>;
  }

}

export default Form;